import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store';
import { KrarkConfiguration, ProbabilityTree, ExecutionState, defaultConfiguration } from 'types';
import { findNodeById } from 'utils/treeBuilder';

interface ConfigState {
  // Configuration
  config: KrarkConfiguration;

  // UI State
  showLoadingScreen: boolean;
  showConfiguration: boolean;
  showPlanner: boolean;
  isExecuting: boolean;

  // Tree data
  tree: ProbabilityTree | null;
  executionState: ExecutionState | null;
}

const initialState: ConfigState = {
  config: defaultConfiguration(),
  showLoadingScreen: true,
  showConfiguration: false,
  showPlanner: false,
  isExecuting: false,
  tree: null,
  executionState: null,
};

export const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    // Configuration updates
    updateConfig: (state, action: PayloadAction<Partial<KrarkConfiguration>>) => {
      state.config = { ...state.config, ...action.payload };
    },

    setKrarksInPlay: (state, action: PayloadAction<number>) => {
      state.config.krarksInPlay = Math.max(0, Math.min(10, action.payload));
    },

    setHarmonicProdigies: (state, action: PayloadAction<number>) => {
      state.config.harmonicProdigies = Math.max(0, Math.min(10, action.payload));
    },

    setFreeCopies: (state, action: PayloadAction<number>) => {
      state.config.freeCopies = Math.max(0, Math.min(20, action.payload));
    },

    setKrarksThumbs: (state, action: PayloadAction<number>) => {
      state.config.krarksThumbs = Math.max(0, Math.min(10, action.payload));
    },

    updateAdvancedConfig: (state, action: PayloadAction<{ path: string; value: number }>) => {
      const { path, value } = action.payload;
      const parts = path.split('.');
      let current: any = state.config.advanced;

      for (let i = 0; i < parts.length - 1; i++) {
        current = current[parts[i]];
      }

      current[parts[parts.length - 1]] = Math.max(0, value);
    },

    // Navigation
    dismissLoadingScreen: (state) => {
      state.showLoadingScreen = false;
      state.showConfiguration = true;
    },

    startPlanning: (state) => {
      state.showConfiguration = false;
      state.showPlanner = true;
    },

    backToConfiguration: (state) => {
      state.showPlanner = false;
      state.showConfiguration = true;
      state.isExecuting = false;
      state.executionState = null;
    },

    executeDoubleOrNothing: (state) => {
      if (!state.tree) return;

      state.isExecuting = true;
      state.executionState = {
        currentNodeId: state.tree.root.id,
        history: [state.tree.root.id],
        isComplete: false,
      };
    },

    makeDecision: (
      state,
      action: PayloadAction<{ nodeId: string; decision: 'continue' | 'stop' }>,
    ) => {
      if (!state.executionState || !state.tree) return;

      const { nodeId, decision } = action.payload;
      const node = findNodeById(state.tree.root, nodeId);
      if (!node) return;

      node.userDecision = decision;

      // If stopping, mark as complete
      if (decision === 'stop') {
        state.executionState.isComplete = true;
        state.executionState.finalResult = node;
      } else {
        // Move to next node
        const nextNode = node.children[0];
        if (nextNode) {
          state.executionState.currentNodeId = nextNode.id;
          state.executionState.history.push(nextNode.id);
        }
      }
    },

    resetExecution: (state) => {
      state.isExecuting = false;
      state.executionState = null;
    },

    // Tree operations
    setTree: (state, action: PayloadAction<ProbabilityTree>) => {
      state.tree = action.payload;
    },

    clearTree: (state) => {
      state.tree = null;
    },
  },
});

// Export actions
export const {
  updateConfig,
  setKrarksInPlay,
  setHarmonicProdigies,
  setFreeCopies,
  setKrarksThumbs,
  updateAdvancedConfig,
  dismissLoadingScreen,
  startPlanning,
  backToConfiguration,
  executeDoubleOrNothing,
  makeDecision,
  resetExecution,
  setTree,
  clearTree,
} = configSlice.actions;

// Configure store
export const store = configureStore({
  reducer: {
    config: configSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types if needed
        ignoredActions: ['config/setTree'],
        // Ignore these paths in the state
        ignoredPaths: ['config.tree'],
      },
    }),
});

// Selectors
export const selectConfig = (state: RootState) => state.config.config;
export const selectShowLoadingScreen = (state: RootState) => state.config.showLoadingScreen;
export const selectShowConfiguration = (state: RootState) => state.config.showConfiguration;
export const selectShowPlanner = (state: RootState) => state.config.showPlanner;
export const selectIsExecuting = (state: RootState) => state.config.isExecuting;
export const selectTree = (state: RootState) => state.config.tree;
export const selectExecutionState = (state: RootState) => state.config.executionState;
